import { useEffect, useState, useCallback } from 'react';
import { Loader2, BookOpen, Clock, AlertCircle, RefreshCw } from 'lucide-react';
import { api, extractApiErrorMessage } from '../../api/client';

interface AulaSlot {
  horario: string;
  materia: string;
  professor: string;
  sala?: string;
  isAtual?: boolean;
}

interface GradeData {
  turma: string;
  turno: string;
  diaSemana: string;
  aulas: AulaSlot[];
  frequencia: {
    percentual: number;
    presentes: number;
    ausentes: number;
    total: number;
  };
}

export function AlunoGrade() {
  const [data, setData] = useState<GradeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const res = await api.get('/mobile/alunos/meu-dashboard');
      setData(res.data);
    } catch (err: any) {
      const msg = extractApiErrorMessage(err, 'Não foi possível carregar a grade de horários.');
      setErro(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
        <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: '#7c3aed' }} />
      </div>
    );
  }

  if (erro && !data) {
    return (
      <div className="m-empty m-animate" style={{ padding: '60px 20px', textAlign: 'center' }}>
        <div className="m-empty-icon" style={{ background: '#fee2e2', color: '#dc2626', margin: '0 auto 12px' }}>
          <AlertCircle size={26} />
        </div>
        <p style={{ fontWeight: 700, color: '#1e293b', marginBottom: 4, fontSize: '0.95rem' }}>
          Falha de Carregamento
        </p>
        <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 16, lineHeight: 1.4, maxWidth: 300, margin: '0 auto 16px' }}>
          {erro}
        </p>
        <button
          type="button"
          onClick={() => void load()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 16px',
            borderRadius: 10,
            border: 'none',
            background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
            color: '#ffffff',
            fontWeight: 700,
            fontSize: '0.82rem',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)',
          }}
        >
          <RefreshCw size={14} />
          Tentar novamente
        </button>
      </div>
    );
  }

  const freq = data?.frequencia;
  const porcentagem = freq?.percentual ?? 0;
  const corFreq = porcentagem >= 85 ? '#16a34a' : porcentagem >= 75 ? '#d97706' : '#dc2626';

  return (
    <div className="m-animate">
      <p className="m-section-title">Minha Frequência</p>

      {/* Frequência visual */}
      <div className="m-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Frequência acumulada</span>
          <span style={{ fontSize: '1.4rem', fontWeight: 800, color: corFreq }}>{porcentagem}%</span>
        </div>
        <div className="m-freq-bar">
          <div className="m-freq-fill" style={{ width: `${porcentagem}%`, background: corFreq }} />
        </div>
        {freq && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: '0.75rem', color: '#64748b' }}>
            <span>{freq.presentes} presentes</span>
            <span>{freq.ausentes} ausentes</span>
            <span>{freq.total} dias</span>
          </div>
        )}
      </div>

      {/* Grade do dia */}
      <p className="m-section-title" style={{ marginTop: 20 }}>
        Grade de Hoje — {data?.diaSemana || new Date().toLocaleDateString('pt-BR', { weekday: 'long' })}
      </p>

      {data?.aulas?.length ? (
        data.aulas.map((aula, i) => (
          <div
            key={i}
            className="m-card"
            style={aula.isAtual ? { borderLeft: '3px solid #7c3aed', background: '#faf5ff' } : {}}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                className="m-card-icon"
                style={{
                  background: aula.isAtual ? '#ede9fe' : '#f1f5f9',
                  color: aula.isAtual ? '#7c3aed' : '#64748b',
                }}
              >
                {aula.isAtual ? <BookOpen size={18} /> : <Clock size={18} />}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>{aula.materia}</div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 2 }}>
                  {aula.professor} {aula.sala ? `· Sala ${aula.sala}` : ''}
                </div>
              </div>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: aula.isAtual ? '#7c3aed' : '#9ca3af' }}>
                {aula.horario}
              </div>
            </div>
            {aula.isAtual && (
              <div style={{ marginTop: 8, fontSize: '0.72rem', color: '#7c3aed', fontWeight: 700 }}>
                ● AULA ATUAL
              </div>
            )}
          </div>
        ))
      ) : (
        <div className="m-empty">
          <div className="m-empty-icon"><BookOpen size={24} /></div>
          <p>Sem aulas registradas para hoje</p>
        </div>
      )}
    </div>
  );
}
